import styled from "styled-components";

import {
  TriangleRedIcon,
  TriangleBlueIcon,
} from "assets/icons/businessDashboard";
import type { ChangeRate } from "types/BusinessDashboard";

const IndicatorSpan = styled.span<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  font-size: 13px;
  font-weight: bold;
  line-height: 18px;
  letter-spacing: -0.2px;
  color: ${({ color }) => color || "#6b7280"};
`;

export const ChangeIndicator = ({
  green,
  changeRate,
  showIcon,
  showKrUnit,
  className,
  neutral = false,
}: {
  green?: boolean;
  showIcon?: boolean;
  showKrUnit?: boolean;
  /**
   * 증감의 좋고 나쁨을 색·화살표로 말하지 않는다.
   * 환불·취소처럼 증감 방향의 의미가 다른 분류와 반대인 줄에 쓴다.
   */
  neutral?: boolean;
  changeRate?: ChangeRate;
  className?: string;
}) => {
  if (!changeRate) return null;

  // 중립 표기: 부호와 수치만 보여주고 색·화살표로 방향을 해석하지 않는다.
  if (neutral) {
    const neutralValue =
      changeRate.calculable === null
        ? "0.0%"
        : changeRate.calculable
          ? `${(changeRate.value ?? 0) > 0 ? "+" : ""}${(changeRate.value ?? 0).toFixed(1)}%`
          : `${changeRate.positive === true ? "∞" : "-∞"}%`;

    return (
      <IndicatorSpan color="#6b7280" className={className}>
        {neutralValue}
      </IndicatorSpan>
    );
  }

  if (changeRate.calculable === null) {
    return (
      <IndicatorSpan color="#6b7280" className={className}>
        0.0%
      </IndicatorSpan>
    );
  }

  if (!changeRate.calculable) {
    const isPositive = changeRate.positive === true;
    const Icon = isPositive ? TriangleBlueIcon : TriangleRedIcon;
    return (
      <IndicatorSpan
        color={isPositive ? (green ? "#00BAA4" : "#1F6AFF") : "#FD4235"}
        className={className}
      >
        {showIcon && <Icon />} {isPositive ? "∞" : "-∞"}%
      </IndicatorSpan>
    );
  }

  const value = changeRate.value ?? 0;

  if (value > 0) {
    return (
      <IndicatorSpan
        color={green ? "#00BAA4" : "#1F6AFF"}
        className={className}
      >
        {showIcon && <TriangleBlueIcon />} {showKrUnit ? "" : "+"}
        {value.toFixed(1)}% {showKrUnit && " 상승"}
      </IndicatorSpan>
    );
  }

  if (value < 0) {
    const valueString = value.toString();
    const unitValue = showKrUnit ? valueString.replace("-", "") : valueString;
    return (
      <IndicatorSpan color="#FD4235" className={className}>
        {showIcon && <TriangleRedIcon />}
        {Number(unitValue).toFixed(1)}%{showKrUnit && " 하락"}
      </IndicatorSpan>
    );
  }

  return (
    <IndicatorSpan color="#6b7280" className={className}>
      {value.toFixed(1)}%
    </IndicatorSpan>
  );
};
